import { RbacPage } from 'app/core/rbac/rbac-page-names.const';
import { SlateIntentDefinition } from './slate.types';

/**
 * Keywords = common people phrasing (word-order variants, casual speech).
 * Keep phrases specific enough to avoid collisions; metric NLP also boosts intents.
 */
export const SLATE_INTENTS: SlateIntentDefinition[] = [
    {
        id: 'film_analytics',
        label: 'Film analytics',
        audience: 'both',
        rbacPage: RbacPage.DASHBOARD,
        keywords: [
            'film analytics',
            'film views',
            'movie views',
            'top films',
            'top movies',
            'dashboard film',
            'views',
            'view count',
            'number of views',
            'how many views',
            'plays',
            'play count',
            'watch count',
            'times watched',
            'minutes watched',
            'watch minutes',
            'watching minutes',
            'watched minutes',
            'how many minutes',
            'how many watch minutes',
            'watch time',
            'watching time',
            'time watched',
            'viewing time',
            'viewing minutes',
            'play time',
            'playback time',
            'watch duration',
            'hours watched',
            'film performance',
            'my film analytics',
            'my views',
            'my watch time',
            'revenue',
            'earnings',
            'income',
            'money made',
            'my revenue',
            'filmmaker revenue',
            'box office',
            'purchases',
            'tickets sold',
            'how many purchases',
            'how many sales',
            'ticket count',
            'unique views',
            'unique viewers',
            'film metrics',
            'views for',
            'analytics for',
            'revenue for',
            'minutes for',
            'show metrics',
            'show analytics',
        ],
        weight: 1.2,
    },
    {
        id: 'webseries_analytics',
        label: 'Web series analytics',
        audience: 'both',
        rbacPage: RbacPage.DASHBOARD,
        keywords: [
            'web series analytics',
            'series analytics',
            'webseries',
            'season views',
            'series views',
            'top seasons',
            'series watch minutes',
            'series minutes watched',
            'web series views',
            'series performance',
            'series metrics',
        ],
        weight: 1.15,
    },
    {
        id: 'user_analytics',
        label: 'User analytics',
        audience: 'admin',
        rbacPage: RbacPage.DASHBOARD,
        keywords: [
            'user analytics',
            'user growth',
            'new users',
            'how many users',
            'how many new users',
            'viewer growth',
            'signups',
            'sign ups',
            'registrations',
            'active users',
            'new viewers',
        ],
        weight: 1.1,
    },
    {
        id: 'film_stats',
        label: 'Film statistics',
        audience: 'both',
        rbacPage: RbacPage.FILM_LIST,
        keywords: [
            'film statistics',
            'film stats',
            'how many films',
            'pending films',
            'approved films',
            'film counts',
            'my film stats',
            'total films',
            'film count',
        ],
        weight: 1.05,
    },
    {
        id: 'film_list',
        label: 'Film list',
        audience: 'both',
        rbacPage: RbacPage.FILM_LIST,
        keywords: [
            'list films',
            'show films',
            'film list',
            'movies',
            'my films',
            'search films',
            'find films',
            'drafted films',
            'show my films',
            'all films',
            'list my films',
            'list my all films',
            'show my all films',
            'my all films',
            'approved films',
            'films with status',
            'ready for approve',
            'ready to approve',
            'ready for approval',
            'movies ready for approve',
            'films ready for approve',
            'which movies are ready',
            'submitted films',
            'list submitted films',
        ],
        weight: 1.15,
    },
    {
        id: 'webseries_list',
        label: 'Web series list',
        audience: 'both',
        rbacPage: RbacPage.FILM_LIST,
        keywords: [
            'list web series',
            'show series',
            'web series list',
            'my series',
            'list series',
            'show my series',
            'all series',
            'web series',
            'my web series',
            'all web series',
            'list my web series',
            'list my all web series',
            'show my web series',
            'show my all web series',
            'my all web series',
            'approved web series',
            'series with status',
            'webseries',
            'my webseries',
        ],
        weight: 1.25,
    },
    {
        id: 'user_list',
        label: 'User list',
        audience: 'admin',
        rbacPage: RbacPage.FILM_LIST,
        keywords: [
            'list filmmakers',
            'list viewers',
            'list admins',
            'list curators',
            'list channel partners',
            'show filmmakers',
            'show viewers',
            'user list',
            'filmmaker list',
            'viewer list',
            'content curator list',
            'channel partner list',
        ],
        weight: 1,
    },
    {
        id: 'views_report',
        label: 'Views report',
        audience: 'admin',
        rbacPage: RbacPage.VIEWS_REPORT,
        keywords: [
            'views report',
            'film views report',
            'view count report',
            'film wise count',
            'views report page',
        ],
        weight: 1.1,
    },
    {
        id: 'watch_activity',
        label: 'Watch activity',
        audience: 'admin',
        rbacPage: RbacPage.WATCH_ACTIVITY_REPORT,
        keywords: [
            'watch activity',
            'watch activity report',
            'watching activity',
            'watch history report',
            'watch history',
            'viewer activity',
        ],
        weight: 1.1,
    },
    {
        id: 'purchase_report',
        label: 'Purchase report',
        audience: 'admin',
        rbacPage: RbacPage.PURCHASE_REPORT,
        keywords: [
            'purchase report',
            'content purchase',
            'film purchase report',
            'ticket purchase',
            'sales report',
            'purchase summary',
        ],
        weight: 1.1,
    },
    {
        id: 'open_content',
        label: 'Open film or series',
        audience: 'both',
        rbacPage: RbacPage.FILM_LIST,
        keywords: [
            'open film',
            'open movie',
            'open series',
            'open web series',
            'view mode',
            'edit mode',
            'open in view',
            'open in edit',
            'go to film',
            'navigate to film',
            'please open',
            'show film page',
        ],
        weight: 1.35,
    },
    {
        id: 'film_lookup',
        label: 'Content details',
        audience: 'both',
        rbacPage: RbacPage.FILM_LIST,
        keywords: [
            'who is the film maker',
            'who is the filmmaker',
            'filmmaker of',
            'film maker of',
            'status of',
            'purchases for',
            'how many purchases',
            'sales of',
            'views of',
            'tell me about',
            'details of',
            'info about',
            'who made',
            'who uploaded',
            'web series status',
            'series purchases',
            'series details',
            'what is status',
        ],
        weight: 1.4,
    },
    {
        id: 'user_lookup',
        label: 'Find user',
        audience: 'admin',
        rbacPage: RbacPage.FILMMAKERS,
        keywords: [
            'find viewer',
            'find filmmaker',
            'search user',
            'who is this number',
            'who is mobile',
            'who is user',
            'mobile number',
            'phone number',
            'open viewer',
            'open filmmaker',
            'open channel partner',
            'open content curator',
            'look up user',
            'user with phone',
            'user with mobile',
        ],
        weight: 1.45,
    },
    {
        id: 'wallet_balance',
        label: 'Wallet & level',
        audience: 'filmmaker',
        keywords: [
            'wallet',
            'wallet balance',
            'current wallet',
            'current wallet balance',
            'my wallet',
            'my wallet balance',
            'tell me my wallet',
            'reel balance',
            'my level',
            'cash balance',
            'my cash',
            'my reels',
            'how much cash',
            'how much balance',
        ],
        weight: 1.55,
    },
    {
        id: 'referral_code',
        label: 'Referral code',
        audience: 'filmmaker',
        keywords: [
            'referral code',
            'my referral',
            'my referral code',
            'what is my referral',
            'refer code',
            'invite code',
            'sharing code',
        ],
        weight: 1.6,
    },
    {
        id: 'help_support',
        label: 'Help & support',
        audience: 'both',
        keywords: [
            'help and support',
            'customer support',
            'contact support',
            'support team',
            'need help',
            'talk to support',
            'call support',
            'email support',
            'whatsapp support',
            'contact us',
            'support desk',
            'supportdesk',
            'wrong data',
            'data is wrong',
            'not correct',
            'do not understand',
            "don't understand",
            'talk to a person',
            'connect with support',
        ],
        weight: 1.55,
    },
    {
        id: 'my_access',
        label: 'My Studio access',
        audience: 'admin',
        keywords: [
            'what can i access',
            'what access do i have',
            'my permissions',
            'my rbac',
            'which pages can i open',
            'what am i allowed',
            'show my access',
        ],
        weight: 1.45,
    },
    {
        id: 'pending_queue',
        label: 'Pending / review queue',
        audience: 'both',
        keywords: [
            'films in review',
            'in review films',
            'pending films',
            'my drafts',
            'draft films',
            'submitted films',
            'waiting for approval',
            'approval queue',
            'how many drafts',
            'how many in review',
        ],
        weight: 1.4,
    },
    {
        id: 'film_moderation',
        label: 'Approve / reject / convert film',
        audience: 'admin',
        rbacPage: RbacPage.FILM_LIST,
        keywords: [
            'should i approve',
            'can i approve',
            'approve this film',
            'approve this movie',
            'reject this film',
            'decline this film',
            'start conversion',
            'convert film',
            'convert movie',
            'start film conversion',
        ],
        weight: 1.55,
    },
    {
        id: 'audit_recent',
        label: 'Recent audit activity',
        audience: 'admin',
        rbacPage: RbacPage.AUDIT_LOG,
        keywords: [
            'who changed',
            'who updated',
            'recent audit',
            'audit trail',
            'audit activity',
            'show audit logs',
            'latest audit',
            'what changed recently',
        ],
        weight: 1.5,
    },
    {
        id: 'route_guide',
        label: 'Studio help',
        audience: 'both',
        keywords: [
            'how do i',
            'how to',
            'where is',
            'where can i',
            'help me',
            'guide',
            'upload film',
            'upload movie',
            'upload season',
            'upload episode',
            'media upload',
            'open dashboard',
            'find report',
            'navigate',
            'take me to',
            'show me how',
            "why can't i",
            'permissions',
            'access control',
            'big short',
            'contest',
            'settings',
            'pricing',
            'rewards settings',
            'approve film',
            'reject film',
            'how to approve',
            'audit log',
            'where is audit',
            'voucher',
            'invoice report',
            'box office means',
            'what is reel',
            'what is cash wallet',
            'tickets means purchases',
            'glossary',
        ],
        weight: 1.25,
    },
];
